import {
  faCircleExclamation,
  faCircleInfo,
  faCircleXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useAtom } from 'jotai';
import { type SyntheticEvent, useState } from 'react';

import { showAlertAtom } from '../../Administration.store';
import useStyles from './SyncDevicesForm.styles';

export interface CustomNotificationProps {
  buttonType: string;
  message: string;
  title: string;
  severity: string;
}

const useCustomNotification = (props: CustomNotificationProps) => {
  const { classes } = useStyles();
  const { buttonType, message, title, severity } = props;
  const [, setShowAlert] = useAtom(showAlertAtom);
  const [promise, setPromise] = useState<null | {
    resolve: (confirmed: boolean) => void;
  }>(null);

  const confirm = () => {
    return new Promise((resolve) => {
      setPromise({ resolve });
    });
  };

  const handleClose = (
    _event: SyntheticEvent<Element, Event>,
    reason?: string
  ) => {
    if (reason === 'escapeKeyDown') {
      return;
    }
    setShowAlert(false);
    promise?.resolve(false);
    setPromise(null);
  };

  const handleConfirm = (_event: SyntheticEvent<Element, Event>) => {
    promise?.resolve(true);
    handleClose(_event, 'backdropClick');
  };

  const handleCancel = (_event: SyntheticEvent<Element, Event>) => {
    promise?.resolve(false);
    handleClose(_event, 'backdropClick');
  };

  const ConfirmationModal = () => (
    <Dialog open={promise !== null} onClose={handleClose}>
      <DialogTitle>
        {severity === 'info' && (
          <FontAwesomeIcon
            style={{ paddingRight: '.75rem' }}
            icon={faCircleInfo}
            size="lg"
            className={classes.infoColor}
          />
        )}
        {severity === 'warning' && (
          <FontAwesomeIcon
            style={{ paddingRight: '.75rem' }}
            icon={faCircleExclamation}
            size="lg"
            className={classes.warningColor}
          />
        )}
        {severity === 'error' && (
          <FontAwesomeIcon
            style={{ paddingRight: '.75rem' }}
            icon={faCircleXmark}
            size="lg"
            className={classes.errorColor}
          />
        )}
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ pt: 2, pb: 1 }}>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        {buttonType === 'Close' && (
          <Button color="primary" onClick={handleCancel}>
            Close
          </Button>
        )}
        {buttonType === 'YesNo' && (
          <>
            <Button color="primary" onClick={handleCancel}>
              No
            </Button>
            <Button color="primary" onClick={handleConfirm} variant="contained">
              Yes
            </Button>
          </>
        )}
        {buttonType === 'OkCancel' && (
          <>
            <Button color="primary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleConfirm} variant="contained">
              Ok
            </Button>
          </>
        )}
        {buttonType === 'ConfirmCancel' && (
          <>
            <Button color="primary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleConfirm} variant="contained">
              Confirm
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );

  return { ConfirmationModal, confirm };
};

export default useCustomNotification;
